import React, { useEffect, useState } from 'react'
import ToastMessage from '../../../../common/ToastMessage';
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useCategory from '../../../../store/ReactQueries/categories/useCategory';
import useAddCategories from '../../../../store/ReactQueries/categories/useAddCategories';
import useUpdateCategory from '../../../../store/ReactQueries/categories/useUpdateCategory';
import useDeleteCategory from '../../../../store/ReactQueries/categories/useDeleteCategory';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../../utils/constants';
import { FieldErrors } from '../../../../domain/helpers/Errors';
import { formatDate } from '../../../../common/GlobalFunctions';
import { validateSchema } from '../../../../common/ValidateSchema';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { CategoryInterface } from '../../../../models/TicketModule/Category';
import { categorySchema } from '../../../../domain/ValidationSchemas/Ticket/category';

export const Categories = () => {

    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<CategoryInterface>({
        id: '',
        title: '',
        isDefault: false,
    });

    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });

    let { data: categories, error, refetch } = useCategory(page, pageSize);

    const addCategory = useAddCategories(page, pageSize);
    const updateCategory = useUpdateCategory(page, pageSize);
    const deleteCategory = useDeleteCategory(page, pageSize);

    useEffect(() => {
        if (error) ToastMessage.Failed(`Error: ${error.message}`);
    }, [error]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 200 },
        { field: 'title', headerName: 'Title', type: "string", editable: true, width: 150 },
        {
            field: 'isDefault',
            headerName: 'IsDefault',
            type: 'boolean',
            editable: true,
            renderCell: ({ value }) => (
                <span style={{ color: darkMode ? 'white' : 'black' }}>
                    {value ? `✓` : '✕'}
                </span>
            ),
        },
        { field: 'createdDate', headerName: 'CreatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'updatedDate', headerName: 'UpdatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'tenantId', headerName: 'TenantId', type: "string", editable: false, width: 150 },
        { field: 'authorId', headerName: 'AuthorId', type: "string", editable: false, width: 150 },
        { field: 'modifiedById', headerName: 'ModifiedById', type: "string", editable: false, width: 150 },
    ];

    const addItems = () => {
        setForm({ ...form, title: '', id: '', isDefault: false });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) {
            console.log(error.message);
            ToastMessage.Failed(error.message);
        } else {
            console.log("An unknown error occurred");
            ToastMessage.Failed();
        }
    };

    const handleSave = async () => {
        const isValid = validateSchema(categorySchema, { title: form.title });

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const upForm: CategoryInterface = {
            id: form.id,
            title: form.title,
            isDefault: form.isDefault
        };

        if (upForm.id === '') {
            const { id, ...formData } = upForm;
            addCategory.mutate(formData, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error)
            });
        } else {
            updateCategory.mutate(upForm, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error)
            });
        }

        setModal({ ...modal, add: false, edit: false, view: false });
    }

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language['ToastDeleteItem'])) {
            deleteCategory.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error)
            });
        }
    };

    return (
        <CustomDataGrid
            name={language['Categories']}
            rows={categories || []}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            pageSize={pageSize}
            setForm={setForm}
            setPage={setPage}
            setModal={setModal}
            addItems={addItems}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
            showColumnModal={['title', 'isDefault']}
            hideColumns={['authorId', 'id', 'tenantId', 'modifiedById']}
        />
    );
}
export default Categories;